import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { RootLayout } from '@/components/layout/RootLayout'
import { GlassCard } from '@/components/ui/GlassCard'
import { GlassButton } from '@/components/ui/GlassButton'
import { GlassInput } from '@/components/ui/GlassInput'
import { GlassBadge } from '@/components/ui/GlassBadge'
import { providersQueryOptions } from '@/lib/queryOptions'
import { api } from '@/lib/api'
import { Plus, Trash2, X, Key, Star } from 'lucide-react'
import config from '@config'
import { useT } from '@/i18n/provider'

export const Route = createFileRoute('/settings/providers')({
  loader: async ({ context: { queryClient } }) => {
    await queryClient.ensureQueryData(providersQueryOptions())
  },
  component: ProvidersPage,
})

function ProvidersPage() {
  const t = useT()
  const qc = useQueryClient()
  const { data: configs = [] } = useQuery(providersQueryOptions())
  const [showAdd, setShowAdd] = useState(false)
  const [name, setName] = useState('')
  const [providerId, setProviderId] = useState('')
  const [modelId, setModelId] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [isDefault, setIsDefault] = useState(false)
  const [error, setError] = useState('')

  const availableProvider = config.modelProviders.find((p) => p.id === providerId)

  const createMutation = useMutation({
    mutationFn: api.providers.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['providers'] })
      setShowAdd(false)
      resetForm()
    },
    onError: (err) => setError(err.message),
  })

  const deleteMutation = useMutation({
    mutationFn: api.providers.delete,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['providers'] }),
  })

  function resetForm() {
    setName(''); setProviderId(''); setModelId(''); setApiKey(''); setBaseUrl(''); setIsDefault(false); setError('')
  }

  return (
    <RootLayout>
      <div className="space-y-6 max-w-3xl">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-white">{t('settings.providers.title')}</h1>
            <p className="text-sm text-white/40 mt-0.5">{t('settings.providers.subtitle')}</p>
          </div>
          <GlassButton size="sm" onClick={() => setShowAdd(true)}>
            <Plus size={14} />
            {t('settings.providers.addProvider')}
          </GlassButton>
        </div>

        {/* Add form */}
        {showAdd && (
          <GlassCard glow className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-white">{t('settings.providers.addProviderTitle')}</h2>
              <button onClick={() => { setShowAdd(false); resetForm() }} className="text-white/40 hover:text-white">
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <GlassInput
                label={t('settings.providers.displayName')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('settings.providers.displayNamePlaceholder')}
              />
              <div>
                <label className="text-sm font-medium text-white/70 block mb-1.5">{t('settings.providers.providerLabel')}</label>
                <select
                  value={providerId}
                  onChange={(e) => { setProviderId(e.target.value); setModelId('') }}
                  className="glass w-full px-4 py-2.5 text-white text-sm bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60"
                >
                  <option value="">{t('settings.providers.selectProvider')}</option>
                  {config.modelProviders.map((p) => (
                    <option key={p.id} value={p.id} style={{ background: '#1a1a2e' }}>{p.label}</option>
                  ))}
                </select>
              </div>
              {availableProvider && (
                <div>
                  <label className="text-sm font-medium text-white/70 block mb-1.5">{t('settings.providers.modelLabel')}</label>
                  <select
                    value={modelId}
                    onChange={(e) => setModelId(e.target.value)}
                    className="glass w-full px-4 py-2.5 text-white text-sm bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60"
                  >
                    <option value="">{t('settings.providers.selectModel')}</option>
                    {availableProvider.models.map((m) => (
                      <option key={m} value={m} style={{ background: '#1a1a2e' }}>{m}</option>
                    ))}
                  </select>
                </div>
              )}
              <GlassInput
                label={t('settings.providers.apiKeyLabel')}
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={t('settings.providers.apiKeyPlaceholder')}
                hint={t('settings.providers.apiKeyHint')}
              />
              {providerId === 'ollama' && (
                <GlassInput
                  label={t('settings.providers.baseUrlLabel')}
                  value={baseUrl}
                  onChange={(e) => setBaseUrl(e.target.value)}
                  placeholder={t('settings.providers.baseUrlPlaceholder')}
                />
              )}
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="w-4 h-4 rounded accent-violet-500"
              />
              <span className="text-sm text-white/70">{t('settings.providers.setDefault')}</span>
            </label>

            {error && <p className="text-red-400 text-sm">{error}</p>}

            <div className="flex gap-2 justify-end">
              <GlassButton variant="ghost" size="sm" onClick={() => { setShowAdd(false); resetForm() }}>{t('common.cancel')}</GlassButton>
              <GlassButton
                size="sm"
                loading={createMutation.isPending}
                disabled={!name || !providerId || !modelId}
                onClick={() => createMutation.mutate({ name, providerId, modelId, apiKey: apiKey || undefined, baseUrl: baseUrl || undefined, isDefault })}
              >
                {t('settings.providers.saveProvider')}
              </GlassButton>
            </div>
          </GlassCard>
        )}

        {/* Provider list */}
        {configs.length === 0 ? (
          <GlassCard className="text-center py-12">
            <Key size={24} className="text-white/20 mx-auto mb-3" />
            <p className="text-white/40 text-sm">{t('settings.providers.noProviders')}</p>
            <p className="text-white/25 text-xs mt-1">{t('settings.providers.addProviderToEnable')}</p>
          </GlassCard>
        ) : (
          <div className="space-y-3">
            {configs.map((cfg) => (
              <GlassCard key={cfg.id} size="sm" className="flex items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-white text-sm">{cfg.name}</span>
                    {cfg.isDefault && (
                      <GlassBadge variant="accent">
                        <Star size={10} className="mr-0.5" />
                        {t('common.default')}
                      </GlassBadge>
                    )}
                    {!cfg.isActive && <GlassBadge variant="warning">{t('common.inactive')}</GlassBadge>}
                  </div>
                  <p className="text-xs text-white/40 mt-0.5">
                    {cfg.providerId} · {cfg.modelId}
                    {cfg.hasApiKey ? ` · ${t('common.keySet')}` : ` · ${t('common.noApiKey')}`}
                  </p>
                </div>
                <GlassButton
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteMutation.mutate(cfg.id)}
                  loading={deleteMutation.isPending}
                  className="text-red-400 hover:text-red-300"
                >
                  <Trash2 size={14} />
                </GlassButton>
              </GlassCard>
            ))}
          </div>
        )}
      </div>
    </RootLayout>
  )
}
